/**
 * Entities Store Getters
 * Type-safe getters for accessing normalized entities and relationships
 */

import {
  EntitiesState,
  HomeEntity,
  RoomEntity,
  DeviceEntity,
  GatewayEntity,
  Relationships,
} from './types';

export default {
  // ============================================================================
  // Entity Collection Getters
  // ============================================================================

  /**
   * Get all homes as an array
   */
  allHomes(state: EntitiesState): HomeEntity[] {
    return Object.values(state.homes);
  },

  /**
   * Get all rooms as an array
   */
  allRooms(state: EntitiesState): RoomEntity[] {
    return Object.values(state.rooms);
  },

  /**
   * Get all devices as an array
   */
  allDevices(state: EntitiesState): DeviceEntity[] {
    return Object.values(state.devices);
  },

  /**
   * Get all gateways as an array
   */
  allGateways(state: EntitiesState): GatewayEntity[] {
    return Object.values(state.gateways);
  },

  /**
   * Get entire relationships object
   */
  relationships(state: EntitiesState): Relationships {
    return state.relationships;
  },

  // ============================================================================
  // Get Entity by ID
  // ============================================================================

  /**
   * Get home by UID
   */
  getHomeById: (state: EntitiesState) => (homeUid: string): HomeEntity | undefined => {
    return state.homes[homeUid];
  },

  /**
   * Get room by UID
   */
  getRoomById: (state: EntitiesState) => (roomUid: string): RoomEntity | undefined => {
    return state.rooms[roomUid];
  },

  /**
   * Get device by ID
   */
  getDeviceById: (state: EntitiesState) => (deviceId: string): DeviceEntity | undefined => {
    return state.devices[deviceId];
  },

  /**
   * Get gateway by UID
   */
  getGatewayById: (state: EntitiesState) => (gatewayUid: string): GatewayEntity | undefined => {
    return state.gateways[gatewayUid];
  },

  // ============================================================================
  // Relationship-based Getters (Forward Lookups)
  // ============================================================================

  /**
   * Get all rooms for a home
   */
  getRoomsForHome: (state: EntitiesState) => (homeUid: string): RoomEntity[] => {
    const roomIds = state.relationships.homeToRooms[homeUid] || [];
    return roomIds.map(roomId => state.rooms[roomId]).filter(Boolean) as RoomEntity[];
  },

  /**
   * Get all devices for a home
   */
  getDevicesForHome: (state: EntitiesState) => (homeUid: string): DeviceEntity[] => {
    const deviceIds = state.relationships.homeToDevices[homeUid] || [];
    return deviceIds.map(deviceId => state.devices[deviceId]).filter(Boolean) as DeviceEntity[];
  },

  /**
   * Get all devices for a room
   */
  getDevicesForRoom: (state: EntitiesState) => (roomUid: string): DeviceEntity[] => {
    const deviceIds = state.relationships.roomToDevices[roomUid] || [];
    return deviceIds.map(deviceId => state.devices[deviceId]).filter(Boolean) as DeviceEntity[];
  },

  /**
   * Get all devices for a gateway
   */
  getDevicesForGateway: (state: EntitiesState) => (gatewayUid: string): DeviceEntity[] => {
    const deviceIds = state.relationships.gatewayToDevices[gatewayUid] || [];
    return deviceIds.map(deviceId => state.devices[deviceId]).filter(Boolean) as DeviceEntity[];
  },

  /**
   * Get all gateways for a home
   */
  getGatewaysForHome: (state: EntitiesState) => (homeUid: string): GatewayEntity[] => {
    const gatewayIds = state.relationships.homeToGateways[homeUid] || [];
    return gatewayIds.map(gatewayId => state.gateways[gatewayId]).filter(Boolean) as GatewayEntity[];
  },

  // ============================================================================
  // Relationship-based Getters (Reverse Lookups)
  // ============================================================================

  /**
   * Get gateway that owns a device
   */
  getGatewayForDevice: (state: EntitiesState) => (deviceId: string): GatewayEntity | null => {
    const gatewayUid = state.relationships.deviceToGateway[deviceId];
    return gatewayUid ? state.gateways[gatewayUid] || null : null;
  },

  /**
   * Get room that contains a device
   */
  getRoomForDevice: (state: EntitiesState) => (deviceId: string): RoomEntity | null => {
    const roomUid = state.relationships.deviceToRoom[deviceId];
    return roomUid ? state.rooms[roomUid] || null : null;
  },

  /**
   * Get home that contains a device
   */
  getHomeForDevice: (state: EntitiesState) => (deviceId: string): HomeEntity | null => {
    const homeUid = state.relationships.deviceToHome[deviceId];
    return homeUid ? state.homes[homeUid] || null : null;
  },

  /**
   * Get home that contains a room
   */
  getHomeForRoom: (state: EntitiesState) => (roomUid: string): HomeEntity | null => {
    const homeUid = state.relationships.roomToHome[roomUid];
    return homeUid ? state.homes[homeUid] || null : null;
  },

  /**
   * Get home that contains a gateway
   */
  getHomeForGateway: (state: EntitiesState) => (gatewayUid: string): HomeEntity | null => {
    const homeUid = state.relationships.gatewayToHome[gatewayUid];
    return homeUid ? state.homes[homeUid] || null : null;
  },

  // ============================================================================
  // Photo URLs
  // ============================================================================

  /**
   * Get all photo URLs by home
   */
  photoUrlByHome(state: EntitiesState): Record<string, string> {
    return state.photoUrlByHome;
  },

  /**
   * Get photo URL for a specific home
   */
  getPhotoForHome: (state: EntitiesState) => (homeUid: string): string | undefined => {
    return state.photoUrlByHome[homeUid];
  },

  /**
   * Get last telemetry for a specific device
   */
  getDeviceTelemetry: (state: EntitiesState) => (deviceId: string) => {
    return state.deviceTelemetry[deviceId] || null;
  },

  // ============================================================================
  // Cache Metadata
  // ============================================================================

  /**
   * Get last fetch timestamp
   */
  lastFetched(state: EntitiesState): number | null {
    return state.lastFetched;
  },

  /**
   * Check if cache is still valid (24 hour TTL)
   */
  isCacheValid(state: EntitiesState): boolean {
    if (!state.lastFetched) return false;
    const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    return Date.now() - state.lastFetched < CACHE_TTL;
  },

  // ============================================================================
  // Legacy Compatibility Getters
  // ============================================================================

  /**
   * Get rooms grouped by home (for backward compatibility)
   * Returns: { [homeUid]: RoomEntity[] }
   */
  roomsByHome(state: EntitiesState): Record<string, RoomEntity[]> {
    const result: Record<string, RoomEntity[]> = {};
    Object.keys(state.relationships.homeToRooms).forEach(homeUid => {
      const roomIds = state.relationships.homeToRooms[homeUid] || [];
      result[homeUid] = roomIds
        .map(roomId => state.rooms[roomId])
        .filter(Boolean) as RoomEntity[];
    });
    return result;
  },
};
